// src/components/risk/RiskDistributionPanel.jsx
// Interactive risk distribution chart showing client counts by category

import React from 'react';
import { TrendingUp, TrendingDown, Minus, Users } from 'lucide-react';
import { RISK_CATEGORIES } from './constants';

const RiskDistributionPanel = ({
  distribution = {},
  totalClients = 0,
  previousDistribution = null,
  onCategoryClick = null,
  selectedCategory = null,
  className = ''
}) => {
  // Calculate max count for scaling bars
  const counts = RISK_CATEGORIES.map(cat =>
    distribution[cat.key] || distribution[cat.name.toLowerCase()] || 0
  );
  const maxCount = Math.max(...counts, 1);

  // Calculate changes from previous period if available
  const getChange = (category) => {
    if (!previousDistribution) return null;
    const current = distribution[category.key] || distribution[category.name.toLowerCase()] || 0;
    const previous = previousDistribution[category.key] || previousDistribution[category.name.toLowerCase()] || 0;
    return current - previous;
  };

  return (
    <div className={`bg-slate-800/50 rounded-xl border border-slate-700 p-4 sm:p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-amber-400" />
          <h3 className="text-lg font-semibold text-white">Risk Distribution</h3>
        </div>
        <span className="text-sm text-slate-400">
          {totalClients.toLocaleString()} total clients
        </span>
      </div>

      {/* Distribution bars */}
      <div className="space-y-3">
        {RISK_CATEGORIES.map((category) => {
          const count = distribution[category.key] || distribution[category.name.toLowerCase()] || 0;
          const percentage = totalClients > 0 ? ((count / totalClients) * 100).toFixed(1) : 0;
          const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;
          const change = getChange(category);
          const isSelected = selectedCategory === category.key;

          return (
            <button
              key={category.key}
              onClick={() => onCategoryClick?.(category.key)}
              className={`w-full text-left transition-all duration-200 ${
                onCategoryClick ? 'cursor-pointer hover:bg-slate-700/30' : ''
              } ${isSelected ? 'bg-slate-700/50 ring-1 ring-amber-500/50' : ''} rounded-lg p-2 -mx-2`}
            >
              {/* Category header */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="text-sm font-medium text-white">
                    {category.name}
                  </span>
                  <span className="text-xs text-slate-500">
                    ({category.min}-{category.max})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Change indicator */}
                  {change !== null && change !== 0 && (
                    <span className={`flex items-center text-xs ${
                      change > 0 ? 'text-red-400' : 'text-green-400'
                    }`}>
                      {change > 0 ? (
                        <TrendingUp className="w-3 h-3 mr-0.5" />
                      ) : (
                        <TrendingDown className="w-3 h-3 mr-0.5" />
                      )}
                      {Math.abs(change)}
                    </span>
                  )}
                  <span className="text-sm font-bold" style={{ color: category.color }}>
                    {count}
                  </span>
                  <span className="text-xs text-slate-500">
                    ({percentage}%)
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: category.color
                  }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Summary stats */}
      <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="text-xl font-bold text-green-400">
            {parseInt(distribution.stable || 0) + parseInt(distribution.low || 0)}
          </div>
          <div className="text-xs text-slate-400">Low Risk</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-yellow-400">
            {parseInt(distribution.moderate || 0) + parseInt(distribution.elevated || 0)}
          </div>
          <div className="text-xs text-slate-400">Medium Risk</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-red-400">
            {parseInt(distribution.high || 0) + parseInt(distribution.critical || 0) + parseInt(distribution.severe || 0)}
          </div>
          <div className="text-xs text-slate-400">High Risk</div>
        </div>
      </div>
    </div>
  );
};

// Compact inline version for dashboard widgets
export const RiskDistributionMini = ({ distribution = {}, className = '' }) => {
  const total = Object.values(distribution).reduce((sum, n) => sum + (Number(n) || 0), 0);

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {RISK_CATEGORIES.map(cat => {
        const count = distribution[cat.key] || distribution[cat.name.toLowerCase()] || 0;
        const width = total > 0 ? (count / total) * 100 : 0;
        if (width === 0) return null;
        return (
          <div
            key={cat.key}
            className="h-2 rounded-full transition-all duration-300"
            style={{
              width: `${width}%`,
              backgroundColor: cat.color,
              minWidth: width > 0 ? '4px' : 0
            }}
            title={`${cat.name}: ${count}`}
          />
        );
      })}
    </div>
  );
};

export default RiskDistributionPanel;
